import { FC, useEffect, useMemo, useReducer, useRef, useState, } from "react";
// 组件
import { ProTable } from '../antd';
import SuperForm from "../SuperForm";
import Toolbar from "./subpage/toolbar";
// 方法
import classNames from "classnames";
import useCardTableReducer, { initCardTable } from './hooks/useCardTableReducer';
import getColumns from "./hooks/getColumns";
import useResizeChange from '../Hooks/useResizeChange';
import { getElementTop } from "../utils/utils";
// 常量
import { CardTableProps } from "./interface";
import { SUCCESS_STATUS_CODE } from "../constants/constant";
import { FormInstance } from "antd/lib/form";

/** 看板列表 */
const InternalCardTable: FC<CardTableProps> = props => {
  const {
    loading, params, dataSource, dataKey, columns, actionRef, statueFiled, autoHeight, wrapClassName, wrapStyle, isCount,
    maxHeight: outMaxHeight, formItems, highWidth, highSearch, operationProps, toolBar, headOperation, showHelpIcon,
    copyColumns,
    fetchApi, formatData, onBefore, onClickOperation, onSearch, onReset, onRefresh,
  } = props;
  const [maxHeight, setMaxHeight] = useState<number>();
  const [reducerState, dispatch] = useReducer(useCardTableReducer, { ...initCardTable, statueFiled, });
  const containerRef = useRef<HTMLDivElement>(null); // 最外层容器
  const searchFormRef = useRef<FormInstance>(); // 搜索框
  const paramsRef = useRef<any>(); // 搜索参数
  useEffect(() => {
    if (dataSource) {
      dispatch({ type: 'CHANGE_DATA', payload: { dataSource, }, });
      return;
    }
    handleFetch();
  }, [ dataSource, fetchApi, params, ]);
  useEffect(() => {
    handleSetHeight();
  }, []);
  // 监听窗口变化
  useResizeChange(() => handleSetHeight());
  /** 计算表格高度 */
  const handleSetHeight = () => {
    if (!autoHeight) return;
    const el = containerRef.current;
    if (!el) return;
    const tableEl = el.querySelector(".ant-table-tbody") as HTMLDivElement;
    const elStyle = window.getComputedStyle(el);
    const footerHeight = parseInt(elStyle.marginBottom) + parseInt(elStyle.paddingBottom);
    let tableHeight = document.body.clientHeight - getElementTop(tableEl) - footerHeight - 8;
    if (outMaxHeight && tableHeight > outMaxHeight) {
      tableHeight = outMaxHeight;
    }
    setMaxHeight(tableHeight);
  };
  /** 获取数据 */
  const handleFetch = async () => {
    if (!fetchApi) return;
    let nextParams = { ...params, ...paramsRef.current,};
    if (onBefore) {
      nextParams = onBefore(nextParams);
      if (!nextParams) return;
    }
    dispatch({ type: 'FETCH_INIT', });
    const res = await fetchApi(nextParams);
    if (res.code === SUCCESS_STATUS_CODE) {
      let nextDataSource = dataKey ? res.data?.[dataKey] : res.data;
      if (formatData) {
        nextDataSource = formatData(nextDataSource ?? []);
      }
      dispatch({ type: 'FETCH_SUCCESS', payload: { dataSource: nextDataSource, }, });
      return;
    }
    dispatch({ type: 'FETCH_ERROR', });
  };
  /** 点击搜索 */
  const handleSearch = (values: any) => {
    paramsRef.current = values;
    handleFetch();
    onSearch?.(values);
  };
  /** 点击刷新按钮 */
  const handleRefresh = () => {
    handleFetch();
    onRefresh?.();
  };
  /** 点击重置 */
  const handleReset = () => {
    paramsRef.current = undefined;
    handleFetch();
    onReset?.();
  };
  /** 生成列 */
  const tableColumns = useMemo(() => {
    return getColumns(columns, maxHeight, isCount, reducerState.dataSource, onClickOperation);
  }, [columns, maxHeight, isCount, reducerState.dataSource]);
  if (actionRef) {
    actionRef.current = {};
  }

  return (
    <div className={classNames("card-table-wrapper", wrapClassName)} ref={containerRef} style={wrapStyle}>
      {formItems && !!formItems.length && (
        <SuperForm.TableForm
          formItems={formItems}
          onFinish={handleSearch}
          onReset={handleReset}
          actionRef={searchFormRef}
          formType="search"
          filterEmpty={false}
          highWidth={highWidth}
          operationProps={operationProps}
          highSearch={highSearch}
        />
      )}
      {
        <Toolbar
          toolBar={toolBar}
          headOperation={headOperation}
          showHelpIcon={showHelpIcon}
          copyColumns={copyColumns}
          dataSource={reducerState.list}
          onRefresh={handleRefresh}
        />
      }
      <ProTable
        className="card-table"
        loading={loading || reducerState.loading}
        columns={tableColumns}
        dataSource={reducerState.dataSource}
        size="small"
        bordered={true}
        pagination={false}
        scroll={{
          x: 'max-width'
        }}
      />
    </div>
  );
};

InternalCardTable.defaultProps = {
  statueFiled: 'statue',
  autoHeight: true,
  isCount: true,
  toolBar: ['refresh', 'copy'],
};

export default InternalCardTable;
